import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, Linkedin, ArrowRight } from "lucide-react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { useLang } from "../../i18n/LanguageContext";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const RECIPIENTS = [
  "info@adr4solutions.it",
  "info.adr4solutions@gmail.com",
  "adirienzobusiness@gmail.com",
];

const initialState = { name: "", company: "", email: "", phone: "", message: "" };

const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.form.error);
      return;
    }
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      toast.error(t.contact.form.error);
      // eslint-disable-next-line no-console
      console.warn(
        "[ADR Contact] EmailJS not configured. Set REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_TEMPLATE_ID and REACT_APP_EMAILJS_PUBLIC_KEY in frontend/.env"
      );
      return;
    }
    setLoading(true);
    try {
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        company: form.company || "-",
        phone: form.phone || "-",
        message: form.message,
        locale: lang,
        reply_to: form.email,
        to_email: RECIPIENTS.join(", "),
        recipient_primary: RECIPIENTS[0],
        recipient_secondary: RECIPIENTS[1],
        recipient_tertiary: RECIPIENTS[2],
      };
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams, {
        publicKey: PUBLIC_KEY,
      });
      toast.success(t.contact.form.success);
      setForm(initialState);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ADR Contact] EmailJS send failed:", err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[var(--ivory)]" data-testid="contact-section">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <span className="overline" data-testid="contact-overline">{t.contact.overline}</span>
          <h2 className="font-serif text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.08] tracking-tight text-[var(--navy)] mt-6" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <div className="mt-8 w-14 h-px bg-[var(--champagne)]" />
          <p className="mt-8 text-[16px] lg:text-[17px] leading-[1.85] text-[var(--charcoal)]" data-testid="contact-intro">
            {t.contact.intro}
          </p>

          <div className="mt-10 space-y-5 text-[15px]">
            <div className="flex items-start gap-4">
              <Mail className="w-4 h-4 mt-1 text-[var(--champagne)]" strokeWidth={1.5} />
              <div>
                <p className="overline mb-1">{t.contact.labels.email}</p>
                <a href="mailto:info@adr4solutions.it" className="text-[var(--navy)] hover:text-[var(--champagne-hover)] transition-colors" data-testid="contact-email-primary">
                  info@adr4solutions.it
                </a>
                <br />
                <a href="mailto:info.adr4solutions@gmail.com" className="text-[var(--charcoal)] hover:text-[var(--champagne-hover)] transition-colors text-[14px]" data-testid="contact-email-alt">
                  info.adr4solutions@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-4 h-4 mt-1 text-[var(--champagne)]" strokeWidth={1.5} />
              <div>
                <p className="overline mb-1">{t.contact.labels.phone}</p>
                <a href="tel:+393478109745" className="text-[var(--navy)] hover:text-[var(--champagne-hover)] transition-colors" data-testid="contact-phone">
                  +39 347 810 9745
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Linkedin className="w-4 h-4 mt-1 text-[var(--champagne)]" strokeWidth={1.5} />
              <div>
                <p className="overline mb-1">LinkedIn</p>
                <a
                  href="https://www.linkedin.com/company/adr4solutions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--navy)] hover:text-[var(--champagne-hover)] transition-colors block"
                  data-testid="contact-linkedin-company"
                >
                  {t.contact.labels.linkedinCompany}
                </a>
                <a
                  href="https://www.linkedin.com/in/alessandro-di-rienzo/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[var(--charcoal)] hover:text-[var(--champagne-hover)] transition-colors text-[14px]"
                  data-testid="contact-linkedin-personal"
                >
                  {t.contact.labels.linkedinPersonal}
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7 bg-white p-8 lg:p-12 border border-[var(--border-light)]"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">{t.contact.form.name} *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className="adr-input"
                required
                data-testid="form-name"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t.contact.form.company}</label>
              <input
                name="company"
                value={form.company}
                onChange={handleChange}
                className="adr-input"
                data-testid="form-company"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t.contact.form.email} *</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="adr-input"
                required
                data-testid="form-email"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t.contact.form.phone}</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="adr-input"
                data-testid="form-phone"
              />
            </div>
          </div>
          <div className="mt-6">
            <label className="overline block mb-2">{t.contact.form.message} *</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows={5}
              className="adr-input resize-none"
              required
              data-testid="form-message"
            />
          </div>
          <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-[12px] text-[var(--charcoal)]/70 max-w-sm">
              {t.contact.cta}
            </p>
            <button
              type="submit"
              className="btn-primary disabled:opacity-60"
              disabled={loading}
              data-testid="form-submit"
            >
              {loading ? t.contact.form.sending : t.contact.form.submit}
              {!loading && <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
